import { Injectable } from '@angular/core';
import * as AnalysisActions from '@modules/analysis/_store/actions/analysis.action';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TrackingService } from '@services/track/tracking.service';
import { AnalysisService } from '@services/analysis/analysis.service';
import { EventsService } from '@services/analysis/events.service';
import { of } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { catchError, map, mergeMap, tap, switchMap,debounceTime } from 'rxjs/operators';
import * as AnalysisState from '../../_store/selectors';
import { errorQueue } from 'src/app/shared/assets/constants';
import { AnalysisStateData, IAuthorize } from 'src/app/shared/interface/analysis';
import { environment } from 'src/environments/environment';
import { IToken } from 'src/app/shared/interface/token';
import { jwtDecode } from 'jwt-decode';

let info;
const analystActions = {
  field_approved: 'validou',
  image_approve: 'validou',
  field_reproved: 'reprovou',
  image_reprove: 'reprovou',
  field_change: 'alterou',
  field_allow_change: 'desmarcou',
  image_select: 'visualizou',
  document_select: 'selecionou',
  analysis_started: 'iniciou',
  analysis_finished: 'finalizou',
}
const mapBankFields = {
  'candidate/dados-bancarios/banco':'bankCode',
  'candidate/dados-bancarios/digito':'bankDigit'
}
let bankValuesProperties = {
  bankCode: null,
  bankDigit: null,
}

@Injectable()
export class AnalysisEffects {
  constructor(
    private actions$: Actions,
    private trackingService: TrackingService,
    private analysisService: AnalysisService,
    private eventsService: EventsService,
    private store: Store,
  ) { }

  // between(min, max) {
  //   return Math.floor(
  //     Math.random() * (max - min) + min
  //   )
  // }

  /**
   * Get code of bank and digit of account of bank document
   * @param props is datas infos about bank document
   * @returns code of bank and digit of account that went validated
   */
  handlerBankProperties(props: {module: string, documentKey: string, payload: any, document?: any, value?: string}): {bankCode: string, bankDigit: string} | null {
     const bankCode = props?.document?.fields?.data?.at(0)?.value || '0';
     const bankDigit = props?.document?.fields?.data?.at(3)?.value || '-';
     bankValuesProperties = {
      bankCode: bankCode?.split(' -')[0] || '0',
      bankDigit
     };
    if (Object.keys(mapBankFields).includes(props?.payload[0]?.key)) { //Verify if field updated is bank name or digit of account
      let bankField = mapBankFields[props?.payload[0]?.key];
      let bankValue = bankField === 'bankCode' ?  props?.value?.split(' -')[0]: props?.value;
      bankValuesProperties[bankField] = bankValue;
    }
    return Object.values(bankValuesProperties).every((prop) => prop !== null) ? bankValuesProperties : null;
  }

  loadAnalysisStore$ = this.store.pipe(select(AnalysisState.selectAnalysis)).subscribe((res) => {
    info = res.info;
  });

  loadAnalysis$ = createEffect(() =>
    this.actions$.pipe(
      debounceTime(1500),
      ofType(AnalysisActions.loadAnalysis),
      mergeMap((res) => {
        if (this.verifyIdAnalysis(res?.id)) {
          return this.analysisService.getCurrentAnalysis().pipe(
            mergeMap((analysis: AnalysisStateData) => this.analysisService.getAuthorize(analysis.info.cookie_url)
              .pipe(
                tap((authorize) => {
                  const payload = {
                    user_id: authorize?.id,
                    name: authorize?.name,
                    email: authorize?.email,
                  }
                  localStorage.setItem('analyst_email', authorize?.email);
                  localStorage.setItem('analyst_id', authorize?.id);
                  this.trackingService.identify(payload)
                })
              )
              .pipe(
                map((authorize: IAuthorize) => (analysis)),
                switchMap((analysis: AnalysisStateData) => [
                  AnalysisActions.loadAnalysisSuccess({ payload: analysis }),
                  AnalysisActions.tracking({
                    title: 'People - Analysis - Analysis Started',
                    action: 'analysis_started',
                    document: undefined,
                    field: undefined,
                  })
                ]),
                catchError(() =>
                  of(AnalysisActions.loadAnalysisFail({ error: errorQueue }))
                )
              )),
            catchError(() =>
              of(AnalysisActions.loadAnalysisFail({ error: errorQueue }))
            )
          );
        }
        return this.analysisService.getNextAnalysis(res?.id)
        .pipe(
          mergeMap((analysis: AnalysisStateData) => this.analysisService.getAuthorize(analysis.info.cookie_url)
            .pipe(
              map((authorize: IAuthorize) => (analysis)),
              switchMap((analysis: AnalysisStateData) => [
                AnalysisActions.loadAnalysisSuccess({ payload: analysis }),
                AnalysisActions.tracking({
                  title: 'People - Analysis - Analysis Started',
                  action: 'analysis_started',
                  document: undefined,
                  field: undefined,
                }),
              ]),
              catchError(() =>
                of(AnalysisActions.loadAnalysisFail({ error: errorQueue }))
              )
            )
          ),
          catchError(() =>
            of(AnalysisActions.loadAnalysisFail({ error: errorQueue }))
          )
        );
      })
    )
  );

  selectDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalysisActions.selectDocument),
      mergeMap((res) => {
        const response = {
          module: res?.module,
          documentKey: res?.payload?.data?.document,
          payload: res?.payload,
          document: res?.document,
        }
        let bankTrackingProperties;
        if (res?.payload?.data?.document === 'candidate/dados-bancarios') {
          bankTrackingProperties = this.handlerBankProperties(response);
        }
        return this.eventsService.dispatchEvents(res.module, [res.payload]).pipe(
          switchMap(() => [
            AnalysisActions.selectDocumentSuccess({
              document: res.payload.data.document,
            }),
            AnalysisActions.tracking({
              title: 'People - Analysis - Analyst Action',
              action: res.payload.kind,
              document: res.payload.data.document,
              field: undefined,
              bankTrackingProperties: {...bankTrackingProperties}
            }),
          ]),
          catchError((err) => of(AnalysisActions.selectDocumentFail({ error: err })))
        )
        })
    )
  );


  updateFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalysisActions.updateField),
      mergeMap((res) => {
        let bankTrackingProperties
        if (res.documentKey === 'candidate/dados-bancarios') {
          bankTrackingProperties = this.handlerBankProperties(res);
        }
        return this.eventsService.dispatchEvents(res.module, res.payload).pipe(
          switchMap(() => [
            AnalysisActions.updateFieldSuccess({
              documentKey: res.documentKey,
              payload: res.payload,
            }),
            AnalysisActions.tracking({
              title: 'People - Analysis - Analyst Action',
              action: res.payload[0].kind,
              document: res.documentKey,
              field: res.payload[0].key,
              bankTrackingProperties: {...bankTrackingProperties}
            }),
          ]),
          catchError((err) => {
            return of(AnalysisActions.updateFieldFail({
              error: err,
              documentKey: res.documentKey,
              payload: res.payload,
            }))
          }
          )
        )
      })
    )
  );

  timeoutAnalysis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalysisActions.timeoutAnalysis),
      mergeMap((res) =>
        this.eventsService.dispatchEvents(res.module, [res.payload]).pipe(
          switchMap(() => [
            AnalysisActions.timeoutAnalysisSuccess(),
            AnalysisActions.tracking({
              title: 'People - Analysis - Analysis Finished',
              action: 'analysis_finished',
              document: undefined,
              field: undefined,
            }),
          ]),
          catchError((err) => of(AnalysisActions.timeoutAnalysisFail({ error: err })))
        )
      )
    )
  );

  approveVoucher$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalysisActions.approveVoucher),
      mergeMap((res) => {
        const response = {
          module: res?.module,
          documentKey: res?.document?.key,
          payload: {},
          document: res?.document,
        }
        let bankTrackingProperties;
        if (res?.document?.key === 'candidate/dados-bancarios') {
          bankTrackingProperties = this.handlerBankProperties(response);
        }
        return this.eventsService.dispatchEvents(res.module, [res.voucher]).pipe(
          switchMap(() => [
            AnalysisActions.approveVoucherSuccess({ key: res?.voucher?.key, document: res?.document }),
            AnalysisActions.tracking({
              title: 'People - Analysis - Analyst Action',
              action: res.voucher.kind,
              document: res?.document?.key,
              field: res?.voucher?.key,
              bankTrackingProperties: {...bankTrackingProperties}
            }),
          ]),
          catchError((err) => of(AnalysisActions.approveVoucherFail({ error: err })))
        )
        })
    )
  );

  setCurrentVoucher$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalysisActions.setCurrentVoucher),
      mergeMap((res) => {
        const response = {
          module: res?.module,
          documentKey: res?.documentKey,
          payload: res?.payload,
          document: res?.document,
        }
        let bankTrackingProperties;
        if (res?.documentKey === 'candidate/dados-bancarios') {
          bankTrackingProperties = this.handlerBankProperties(response);
        }
        return this.eventsService.dispatchEvents(res.module, [res.payload]).pipe(
          switchMap(() => [
            AnalysisActions.setCurrentVoucherSuccess({
              documentKey: res.documentKey,
              document: res.document,
              voucherKey: res.payload.data.image,
            }),
            AnalysisActions.tracking({
              title: 'People - Analysis - Analyst Action',
              action: res.payload.kind,
              document: res.document.key,
              field: res.document.images.selected,
              bankTrackingProperties: {...bankTrackingProperties}
            }),
          ]),
          catchError((err) =>
            of(AnalysisActions.setCurrentVoucherFail({ error: err }))
          )
        )
        })
    )
  );

  approveEsocial$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalysisActions.approveEsocial),
      mergeMap((res) =>
        this.eventsService.dispatchEvents(res.module, [res.document]).pipe(
          map(() =>
            AnalysisActions.approveEsocialSuccess({ document: res.document, status: res.status, mensage: res.mensage })
          ),
          catchError((err) =>
            of(AnalysisActions.setCurrentVoucherFail({ error: err }))
          )
        )
      )
    )
  );

  refuseVoucher$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalysisActions.reproveVoucher),
      mergeMap((res) => {
        const response = {
          module: res?.module,
          documentKey: res?.payload?.key,
          payload: res?.payload,
          document: res?.document,
        }
        let bankTrackingProperties;
        if (res?.payload?.key === 'candidate/dados-bancarios/comprovantes/frente') {
          bankTrackingProperties = this.handlerBankProperties(response);
        }
        return this.eventsService.dispatchEvents(res.module, [res.payload]).pipe(
          switchMap(() => [
            AnalysisActions.reproveVoucherSuccess({
              documentKey: res.document.key,
              document: res.document,
              target: res.payload.key,
              reasons: res?.payload?.data?.reasons
            }),
            AnalysisActions.tracking({
              title: 'People - Analysis - Analyst Action',
              action: res.payload.kind,
              document: res.document.key,
              field: res.document.images.selected,
              bankTrackingProperties: {...bankTrackingProperties}
            }),
          ]),
          catchError((err) =>
            of(AnalysisActions.reproveVoucherFail({ error: err }))
          )
        )
        })
    )
  );

  tracking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalysisActions.tracking),
      tap((res) => {
        if (!!analystActions[res?.action]) {
          try {
            this.trackingService.track(res.title, {
              organization: info?.profile?.company,
              analysis_id: info?.id,
              action: analystActions[res?.action],
              document: res?.document,
              field: res?.field,
              type: info?.topic,
              analyst_email: localStorage.getItem('analyst_email'),
              ...res?.bankTrackingProperties
            });
          } catch (e) {
            console.warn('Tracking error:', e instanceof Error ? e.message : 'No error message found.');
          }
        }
      })
    ), { dispatch: false });

    trackingEsocial$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AnalysisActions.trackingEsocial),
        tap((res) => {
          try {
            this.trackingService.track(res.title, {...res?.properties});
          } catch (e) {
            console.warn('Tracking error:', e instanceof Error ? e.message : 'No error message found.');
          }
        })
      ), { dispatch: false });

    updateFieldsSystem$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AnalysisActions.updateFieldSystem),
        mergeMap((res) => {
          return this.eventsService.dispatchEvents(res.module, res.payload).pipe(
            switchMap(() => [
              AnalysisActions.updateFieldSuccess({
                documentKey: res.documentKey,
                payload: res.payload,
              }),
              AnalysisActions.tracking({
                title: 'People - Analysis - Analyst Action',
                action: res.payload[0].kind,
                document: res.documentKey,
                field: res.payload[0].key,
              }),
              AnalysisActions.trackingEsocial({
                title: 'People - Analysis - Automated and Social Validation',
                properties: {}
              }),
            ]),
            catchError((err) => {
              return of(AnalysisActions.updateFieldFail({
                error: err,
                documentKey: res.documentKey,
                payload: res.payload,
              }))
            }
            )
          )
        })
      )
    );

    verifyIdAnalysis(id: string): boolean {
      const token = sessionStorage.getItem('state');
      
      if (!token) {
        return false;
      
      }

      const analysisInfo: IToken = jwtDecode(token);
      if (analysisInfo?.AnlId != id) {
        return false;
      }

      return true;
    }
}
